import React, { useState, useEffect } from 'react';
import customHeaders, { getSpace } from '../../common/headers';
import {
  InputDinamycPlaceholder,
  ButtonBasic,
} from '@lk-gtcom/ecomlab-components';
import { ButtonCopy } from '../../components/Buttons/ButtonCopy/ButtonCopy';
import './InformationForBilling.scss';
import { ArtemBusinessData2Https } from '../../fetchUrls';
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { useResponseHandler } from '../../common/auth';
import useGeneralStore from '../../store/general';
import { usePushNotifications } from '../../common/pushNotification';

interface BusinessData {
  org_type?: string;
  НаимСокр?: string;
  НаимПолн?: string;
  ИНН?: string;
  КПП?: string;
  [key: string]: any;
}

interface FieldItem {
  label: string;
  value: string | undefined;
  copy?: boolean;
}

const InformationForBilling: React.FC = ({
  modal = false,
  isEdit = false
}) => {
  const selectedSpace = useGeneralStore((state) => state.selectedSpace);
  const headers = { ...customHeaders, ...getSpace(selectedSpace) };
  const { handleTableResponse, handleRequestError } = useResponseHandler();
  const { addErrorPush, addSuccessPush } = usePushNotifications();

  const [editable, setEditable] = useState<boolean>(isEdit);
  const [data, setData] = useState<BusinessData | null>(null);
  const [inn, setInn] = useState<string | null>(null);
  const [haveData, setHaveData] = useState<boolean>(false);
  const [innData, setInnData] = useState<BusinessData | null>(null);
  const [listField, setListField] = useState<FieldItem[]>([]);

  const fetchGetBusinessData = (): void => {
    const url = `${ArtemBusinessData2Https}/api/v2/business-data/get`;

    fetch(url, { method: 'GET', headers })
      .then(handleTableResponse)
      .then((json) => setData(json.data))
      .catch(handleRequestError);
  };

  const fetchGetPermission = (): void => {
    const url = `${ArtemBusinessData2Https}/api/v2/business-data/permission`;

    fetch(url, { method: 'GET', headers })
      .then(handleTableResponse)
      .then((json) => { })
      .catch(handleRequestError);
  };

  const fetchGetDataByInn = (): void => {
    const url = `${ArtemBusinessData2Https}/api/v2/business-data/search`;

    const body = JSON.stringify({
      org_type: 'org',
      tax_num: inn,
    });

    fetch(url, { method: 'POST', headers, body })
      .then(response => {
        if (response.status === 400) {
          return response.json().then(data => {
            addErrorPush('Данные не найдены')
            throw { status: 400, data };
          });
        }
        return handleTableResponse(response);
      })
      .then((json) => {
        setInnData(json?.data);
        setHaveData(true);
      })
      .catch(handleRequestError);
  };

  const fetchSaveDataByInn = (): void => {
    const url = `${ArtemBusinessData2Https}/api/v2/business-data/save?tax_num=${inn}`;

    fetch(url, { method: 'POST', headers })
      .then(handleTableResponse)
      .then((json) => {
        setHaveData(false);
        setEditable(false);
        fetchGetBusinessData();
      })
      .catch(handleRequestError);
  };

  const fetchDeleteBusinessData = (): void => {
    const url = `${ArtemBusinessData2Https}/api/v2/business-data/delete`;

    fetch(url, { headers, method: 'DELETE' })
      .then(async (res) => {
        if (res.status === 204) {
          setHaveData(false);
          setEditable(false);
          fetchGetBusinessData();
        } else {
          const err = await res.json();
          throw new Error(JSON.stringify(err));
        }
      })
      .catch(handleRequestError);
  };

  useEffect(() => {
    fetchGetPermission();
    fetchGetBusinessData();
  }, []);

  return (
    <>
      <div className="information-for-billing">
        {!modal && <TitlePage
          title='Информация для выставления счетов и актов'
        />}

        {!modal && <ButtonBasic
          lilac
          width="280px"
          text={!editable ? 'Редактировать юридические данные' : 'Назад'}
          onClick={() => {
            setEditable(!editable);
            setHaveData(false);
          }}
        />}

        <div className="information-for-billing__content">
          <TitlePage
            medium
            title={'Юридическое лицо'}
          />
          {editable ? (
            <>
              <InputDinamycPlaceholder
                onChange={(e: string) => setInn(e)}
                type="text"
                classname="autorization__input-field"
                placeholder="Введите ИНН организации*"
                autoComplete={false}
              />

              <ButtonBasic
                blue
                text='Найти по ИНН'
                onClick={fetchGetDataByInn}
              />
            </>
          ) : (
            <>
              <div className="information-for-billing__info-card">
                {listField.map(({ label, value, copy }) => (
                  <div className="information-for-billing__row" key={label}>
                    <div className="information-for-billing__value-box">
                      <span className="information-for-billing__placeholder">{label}</span>
                      <p className="information-for-billing__value">{value}</p>
                      {copy && <ButtonCopy value={value || ''} />}
                    </div>
                  </div>
                ))}

                <div className="information-for-billing__row">
                  <div className="information-for-billing__value-box">
                    <span className="information-for-billing__placeholder">
                      {'Название организации'}
                    </span>
                    <p className="information-for-billing__text">{data?.['НаимСокр'] || '-'}</p>
                    {data?.['НаимСокр'] && <ButtonCopy value={data?.['НаимСокр']} />}
                  </div>
                </div>
                <div className="information-for-billing__row">
                  <div className="information-for-billing__value-box">
                    <span className="information-for-billing__placeholder">
                      {'Полное название организации'}
                    </span>
                    <p className="information-for-billing__text">{data?.['НаимПолн'] || '-'}</p>
                    {data?.['НаимПолн'] && <ButtonCopy value={data?.['НаимПолн']} />}
                  </div>
                </div>

                <div className="information-for-billing__group">
                  <div className="information-for-billing__row">
                    <div className="information-for-billing__value-box">
                      <span className="information-for-billing__placeholder">ИНН организации</span>
                      <p className="information-for-billing__text">
                        {data?.['ИНН'] || '-'}
                      </p>
                      {data?.['ИНН'] && <ButtonCopy value={data?.['ИНН']} />}
                    </div>
                  </div>
                  <div className="information-for-billing__row">
                    <div className="information-for-billing__value-box">
                      <span className="information-for-billing__placeholder">КПП организации</span>
                      <p className="information-for-billing__text">
                        {data?.['КПП'] || '-'}
                      </p>
                      {data?.['КПП'] && <ButtonCopy value={data?.['КПП']} />}
                    </div>
                  </div>
                </div>

                <div className="information-for-billing__row">
                  <div className="information-for-billing__value-box">
                    <span className="information-for-billing__placeholder">
                      {'Юридический адрес'}
                    </span>
                    <p className="information-for-billing__text">{data?.['АдресРФ'] || '-'}</p>
                    {data?.['АдресРФ'] && <ButtonCopy value={data?.['АдресРФ']} />}
                  </div>
                </div>

              </div>
            </>
          )}
        </div>
      </div>

      {editable && haveData && innData && (
        <div className="information-for-billing__content">
          <InputDinamycPlaceholder
            type="text"
            placeholder="Страна организации"
            autoComplete={false}
            disabled={true}
          />

          <InputDinamycPlaceholder
            changeValue={innData.НаимСокр}
            type="text"
            placeholder="Название организации"
            required
            autoComplete={false}
            disabled={true}
          />

          <InputDinamycPlaceholder
            changeValue={innData.НаимПолн}
            type="text"
            placeholder="Полное название организации"
            required
            autoComplete={false}
            disabled={true}
          />

          <div className="information-for-billing__input-group">
            <InputDinamycPlaceholder
              changeValue={innData.ИНН}
              type="text"
              placeholder="ИНН организации"
              required
              autoComplete={false}
              disabled={true}
            />

            <InputDinamycPlaceholder
              changeValue={innData.КПП}
              type="text"
              placeholder="КПП организации"
              required
              autoComplete={false}
              disabled={true}
            />
          </div>

          <InputDinamycPlaceholder
            changeValue={innData?.['АдресРФ']}
            type="text"
            placeholder="Юридический адрес"
            required
            autoComplete={false}
            disabled={true}
          />

          <InputDinamycPlaceholder
            type="text"
            placeholder="Почтовый адрес"
            autoComplete={false}
            disabled={true}
          />

          <InputDinamycPlaceholder
            type="text"
            placeholder="Телефон"
            autoComplete={false}
            disabled={true}
          />

          <InputDinamycPlaceholder
            type="text"
            placeholder="E-mail бухгалтера"
            autoComplete={false}
            disabled={true}
          />

          <div className="information-for-billing__group-btn">
            <ButtonBasic
              red
              onClick={fetchDeleteBusinessData}
              text='Удалить данные'
            />

            <ButtonBasic
              green
              onClick={fetchSaveDataByInn}
              text='Сохранить данные'
            />
          </div>
        </div>
      )}
    </>
  );
};

export { InformationForBilling };